import React, { useCallback, useEffect, useState } from "react";
import {
  Box, CircularProgress, Grid, Link as MuiLink, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import DescriptionIcon from "@mui/icons-material/Description";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import api from "../../api";
import { toast } from "react-toastify";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsFiltersBar from "./AnalyticsFiltersBar";
import useAnalyticsFilters from "./useAnalyticsFilters";
import { StatusChip } from "./AnalyticsDataTable";

const TILE_CONFIGS = [
  { key: "school_past_papers_available", label: "Total Past Papers",       Icon: DescriptionIcon,  iconBg: "#ede9fe", iconColor: "#7c3aed" },
  { key: "target_school_revision_items", label: "Target School Revision",  Icon: TrackChangesIcon, iconBg: "#dcfce7", iconColor: "#16a34a" },
  { key: "target_topic_revision_items",  label: "Target Topic Revision",   Icon: MenuBookIcon,     iconBg: "#fff7ed", iconColor: "#ea580c" },
  // Paid for, versus opened free through a mobile plan, course, preview or pack.
  { key: "assigned_past_papers",         label: "Past Papers Bought",      Icon: ShoppingCartIcon, iconBg: "#dbeafe", iconColor: "#2563eb" },
  { key: "granted_past_papers",          label: "Past Papers Opened Free", Icon: LockOpenIcon,     iconBg: "#f1f5f9", iconColor: "#475569" },
];

function KpiTile({ label, value, Icon, iconBg, iconColor }) {
  return (
    <Paper sx={{ ...CARD_SX, display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ color: iconColor, fontSize: 26 }} />
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{(value ?? 0).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
      </Box>
    </Paper>
  );
}

export default function RevisionPlanningAnalytics() {
  const [courseId, setCourseId] = useState("");
  const [academicCourseId, setAcademicCourseId] = useState("");
  const [planId, setPlanId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const { courses, academicCourses, plans } = useAnalyticsFilters({ courseId, academicCourseId });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (courseId) params.course_id = courseId;
      if (academicCourseId) params.academic_course_id = academicCourseId;
      if (planId) params.course_plan_id = planId;
      const res = await api.get("admin/analytics/revision-planning", { params });
      setData(res.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load revision planning");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [courseId, academicCourseId, planId]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};

  return (
    <AnalyticsPageShell
      title="Revision Analytics"
      subtitle="Insights into targeted school revision, targeted topic revision, and school past papers."
      filters={
        <AnalyticsFiltersBar
          courses={courses}
          academicCourses={academicCourses}
          plans={plans}
          courseId={courseId}
          setCourseId={setCourseId}
          showAcademicCourse={false}
          academicCourseId={academicCourseId}
          setAcademicCourseId={setAcademicCourseId}
          planId={planId}
          setPlanId={setPlanId}
        />
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {TILE_CONFIGS.map(({ key, label, Icon, iconBg, iconColor }) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <KpiTile label={label} value={summary[key]} Icon={Icon} iconBg={iconBg} iconColor={iconColor} />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Revision by School</Typography>
              <TableContainer component={Paper} sx={CARD_SX}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>School</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Past Papers</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Assigned Students</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Revision Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.revision_by_school || []).map((r) => (
                      <TableRow key={r.school_id} hover>
                        <TableCell>{r.label}</TableCell>
                        <TableCell align="right">{r.past_papers ?? r.count}</TableCell>
                        <TableCell align="right">{r.assigned_students ?? r.count}</TableCell>
                        <TableCell><StatusChip status={r.revision_status} /></TableCell>
                      </TableRow>
                    ))}
                    {!(data?.revision_by_school?.length) && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: "text.secondary", py: 3 }}>No data</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Target Topic Revision</Typography>
              <TableContainer component={Paper} sx={CARD_SX}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Topic</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Worksheets</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Students Assigned</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.revision_by_topic || []).map((r, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{r.topic_name}</TableCell>
                        <TableCell align="right">{r.worksheets ?? r.count}</TableCell>
                        <TableCell align="right">{r.students_assigned ?? r.count}</TableCell>
                        <TableCell><StatusChip status={r.status} /></TableCell>
                      </TableRow>
                    ))}
                    {!(data?.revision_by_topic?.length) && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: "text.secondary", py: 3 }}>No data</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Looking for the subtopics students are struggling with?{" "}
            <MuiLink component={RouterLink} to="/admin/analytics/weak-topics" underline="hover" sx={{ color: "#7c3aed", fontWeight: 600 }}>
              Weak Topics
            </MuiLink>{" "}
            lists them and turns a group into a revision class.
          </Typography>
        </>
      )}
    </AnalyticsPageShell>
  );
}
